import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faExternalLinkAlt, faTimes } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';

const ClientLicensesAlertBanner = ({ licenses = [], isAssigned = true }) => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const [isDismissed, setIsDismissed] = useState(false);

    const expiredLicenses = licenses.filter(lic => !lic.bTienePolizaActiva);
    const hasExpired = Boolean(isAssigned && expiredLicenses.length > 0);

    // Escuchar evento para reabrir la alerta si el asesor le da clic al badge en el navbar superior
    useEffect(() => {
        const handleReopen = () => {
            setIsDismissed(false);
        };
        window.addEventListener('akasia:support:reopen_policies_alert', handleReopen);
        return () => {
            window.removeEventListener('akasia:support:reopen_policies_alert', handleReopen);
        };
    }, []);

    // Notificar al Navbar superior el estado de la alerta (para mostrar u ocultar el badge POLIZAS VENCIDAS)
    useEffect(() => {
        window.dispatchEvent(
            new CustomEvent('akasia:support:policies_alert_state', {
                detail: {
                    isDismissed,
                    hasExpired,
                    count: expiredLicenses.length
                }
            })
        );
    }, [isDismissed, hasExpired, expiredLicenses.length]);

    if (isDismissed || !licenses || licenses.length === 0 || !hasExpired) return null;

    const handleDismiss = () => {
        setIsDismissed(true);
    };

    return (
        <div className="flex-shrink-0 px-2 px-sm-3 pt-2 pb-1">
            <div
                className="position-relative d-flex align-items-start gap-2 gap-sm-3 p-2 p-sm-3 rounded-3"
                style={{
                    backgroundColor: isDark ? 'rgba(239, 68, 68, 0.12)' : '#fdf2f2',
                    border: isDark ? '1px solid rgba(239, 68, 68, 0.35)' : '1px solid #f8b4b4',
                    color: isDark ? '#fca5a5' : '#7f1d1d'
                }}
            >
                {/* Icono de advertencia parpadeante a la izquierda */}
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                        color: '#ffc107',
                        marginTop: '2px',
                        animation: 'blink 1s infinite alternate'
                    }}
                >
                    <style>{`
                        @keyframes blink {
                            0% { opacity: 1; }
                            100% { opacity: 0.2; }
                        }
                    `}</style>

                    <FontAwesomeIcon icon={faExclamationTriangle} className="fs-5 fs-sm-3" />
                </div>

                {/* Contenido central */}
                <div className="flex-grow-1 pe-3 pe-sm-4">
                    {/* Fila superior: Badge + Título */}
                    <div className="d-flex align-items-center gap-1.5 gap-sm-2 flex-wrap mb-1">
                        <span
                            className="badge px-1.5 py-0.5 px-sm-2 py-sm-1 text-white"
                            style={{
                                backgroundColor: isDark ? '#991b1b' : '#b85d61',
                                fontSize: '0.62rem',
                                fontWeight: 700,
                                letterSpacing: '0.03em',
                                borderRadius: '4px'
                            }}
                        >
                            ATENCIÓN AGENTE
                        </span>
                        <span
                            className="fw-bold text-uppercase"
                            style={{
                                fontSize: '0.72rem',
                                letterSpacing: '0.02em',
                                color: isDark ? '#f87171' : '#991b1b'
                            }}
                        >
                            ALERTA DE PÓLIZAS VENCIDAS
                        </span>
                    </div>

                    {/* Mensaje principal responsivo */}
                    <div
                        style={{
                            lineHeight: 1.35,
                            color: isDark ? '#fecaca' : '#6b2121'
                        }}
                    >
                        {/* Texto para pantallas medianas y grandes */}
                        <span className="d-none d-sm-inline" style={{ fontSize: '0.82rem' }}>
                            Este cliente cuenta con licencias con pólizas vencidas. Por favor verifique la licencia a la que se le está brindando soporte antes de continuar.
                        </span>

                        {/* Texto simplificado y más pequeño para móviles */}
                        <span className="d-inline d-sm-none" style={{ fontSize: '0.72rem' }}>
                            Por favor verifique la licencia a la que se le está brindando soporte antes de continuar.
                        </span>
                    </div>

                    {/* Listado de licencias vencidas con enlace directo */}
                    <div className="mt-1 mt-sm-2 d-flex flex-wrap align-items-center gap-1 gap-sm-2">
                        <span
                            className="fw-bold me-1"
                            style={{
                                fontSize: '0.70rem',
                                color: isDark ? '#fca5a5' : '#8c3539'
                            }}
                        >
                            Licencias con póliza vencida:
                        </span>
                        {expiredLicenses.map(lic => {
                            return (
                                <a
                                    key={lic.idLicencia}
                                    href={`/licencias/detalle/${lic.idLicencia}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm py-0 px-1.5 px-sm-2 d-inline-flex align-items-center gap-1 text-decoration-none fw-semibold shadow-none"
                                    style={{
                                        backgroundColor: isDark ? '#1a2736' : '#ffffff',
                                        borderColor: isDark ? '#f87171' : '#fca5a5',
                                        color: isDark ? '#fca5a5' : '#991b1b',
                                        border: '1px solid',
                                        borderRadius: '5px',
                                        fontSize: '0.70rem'
                                    }}
                                    title={`Abrir licencia #${lic.idLicencia} en otra ventana`}
                                >
                                    <span>#{lic.idLicencia} — {lic.vDescripcionTipo}</span>
                                    <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.60rem' }} />
                                </a>
                            );
                        })}
                    </div>
                </div>

                {/* Botón X de cerrar */}
                <button
                    type="button"
                    onClick={handleDismiss}
                    className="btn btn-link p-1 position-absolute shadow-none"
                    style={{
                        top: '4px',
                        right: '6px',
                        color: isDark ? '#fca5a5' : '#b85d61',
                        opacity: 0.8,
                        fontSize: '0.75rem',
                        lineHeight: 1
                    }}
                    title="Ocultar alerta"
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

ClientLicensesAlertBanner.propTypes = {
    licenses: PropTypes.array,
    isAssigned: PropTypes.bool
};

export default ClientLicensesAlertBanner;
